import { useState, useEffect } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Download, Upload, Database, AlertTriangle, CheckCircle, Loader2, FileJson, HardDrive } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function BackupRestore() {
  const { user } = useAuth();
  const [backupInfo, setBackupInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [importResult, setImportResult] = useState(null);

  // Only master users can access this page
  const isMaster = user?.role === "master";

  useEffect(() => {
    if (isMaster) {
      fetchBackupInfo();
    }
  }, [isMaster]);

  const fetchBackupInfo = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/backup/info");
      setBackupInfo(data);
    } catch (err) {
      toast.error("Failed to load backup info");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      toast.info("Preparing backup... This may take a moment.");
      
      // Step 1: Get a one-time download token (this uses cookies for auth)
      const tokenResponse = await api.post("/backup/export/token");
      const { token } = tokenResponse.data;
      
      // Step 2: Use the token to download via direct link (no cookies needed)
      // This bypasses CORS credentialed request issues
      const downloadUrl = `${window.location.origin}/api/backup/download?token=${encodeURIComponent(token)}`;
      
      // Create an invisible link and trigger download
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `dyynit_backup_${new Date().toISOString().slice(0,10)}.json.gz`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      toast.success("Backup download started!");
    } catch (err) {
      console.error("Export error:", err);
      const errorMsg = err.response?.data?.detail || err.message || "Network Error";
      toast.error("Failed to export backup: " + errorMsg);
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith(".json") && !file.name.endsWith(".json.gz")) {
        toast.error("Please select a .json or .json.gz backup file");
        return;
      }
      setSelectedFile(file);
      setShowImportDialog(true);
      setConfirmText("");
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    
    try {
      setImporting(true);
      
      const formData = new FormData();
      formData.append("file", selectedFile);
      
      const { data } = await api.post("/backup/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setImportResult(data);
      
      if (data.success) {
        toast.success(`Database restored! ${data.collections_restored} collections, ${data.documents_restored} documents`);
      } else {
        toast.warning("Import completed with some errors");
      }
      
      // Refresh backup info
      fetchBackupInfo();
      
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      toast.error("Import failed: " + errorMsg);
      setImportResult({
        success: false,
        message: errorMsg,
        collections_restored: 0,
        documents_restored: 0,
        errors: [errorMsg],
      });
    } finally {
      setImporting(false);
    }
  };

  const closeImportDialog = () => {
    setShowImportDialog(false);
    setSelectedFile(null);
    setConfirmText("");
    setImportResult(null);
  };

  if (!isMaster) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            Only master users can access the backup and restore features.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Database className="w-6 h-6" />
          Database Backup & Restore
        </h1>
        <p className="text-muted-foreground mt-1">
          Export your complete database or restore from a previous backup.
        </p>
      </div>

      {/* Database Info */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <HardDrive className="w-5 h-5" />
          Current Database
        </h2>
        
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading database info...
          </div>
        ) : backupInfo ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="text-2xl font-bold font-mono-data">{backupInfo.collection_count}</div>
                <div className="text-sm text-muted-foreground">Collections</div>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="text-2xl font-bold font-mono-data">{backupInfo.total_documents.toLocaleString()}</div>
                <div className="text-sm text-muted-foreground">Documents</div>
              </div>
              <div className="bg-muted/50 p-4 rounded-lg col-span-2">
                <div className="text-lg font-semibold">{backupInfo.estimated_export_time}</div>
                <div className="text-sm text-muted-foreground">Estimated Export Time</div>
              </div>
            </div>
            
            <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                View collection details ({backupInfo.collections.length} collections)
              </summary>
              <div className="mt-2 max-h-48 overflow-y-auto border rounded p-2 bg-muted/30">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-1">Collection</th>
                      <th className="text-right p-1">Documents</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backupInfo.collections.map((c) => (
                      <tr key={c.name} className="border-b border-muted">
                        <td className="p-1 font-mono text-xs">{c.name}</td>
                        <td className="p-1 text-right font-mono-data">{c.document_count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        ) : (
          <div className="text-muted-foreground">Failed to load database info</div>
        )}
      </Card>

      {/* Export Section */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Download className="w-5 h-5 text-green-600" />
          Export Backup
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Download a complete backup of your database as a JSON file. Store this file safely
          to restore your data if needed.
        </p>
        <Button
          onClick={handleExport}
          disabled={exporting || loading}
          className="gap-2"
          data-testid="export-backup-btn"
        >
          {exporting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Exporting...
            </>
          ) : (
            <>
              <FileJson className="w-4 h-4" />
              Download Backup
            </>
          )}
        </Button>
      </Card>

      {/* Import Section */}
      <Card className="p-6 border-orange-200 bg-orange-50/30">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Upload className="w-5 h-5 text-orange-600" />
          Restore from Backup
        </h2>
        
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Warning: Destructive Action</AlertTitle>
          <AlertDescription>
            Restoring a backup will <strong>completely wipe</strong> your current database
            and replace it with the backup data. This action cannot be undone.
          </AlertDescription>
        </Alert>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="backup-file">Select Backup File (.json or .json.gz)</Label>
            <Input
              id="backup-file"
              type="file"
              accept=".json,.json.gz"
              onChange={handleFileSelect}
              className="mt-1"
              data-testid="import-file-input"
            />
          </div>
        </div>
      </Card>

      {/* Import Confirmation Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="w-5 h-5" />
              Confirm Database Restore
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2">
                {!importResult ? (
                  <>
                    <p>
                      You are about to restore the database from:
                    </p>
                    <p className="font-mono text-sm bg-muted p-2 rounded">
                      {selectedFile?.name}
                    </p>
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        This will <strong>permanently delete</strong> all current data and replace
                        it with the backup. This cannot be undone.
                      </AlertDescription>
                    </Alert>
                    <div className="space-y-2">
                      <Label>Type &quot;RESTORE&quot; to confirm:</Label>
                      <Input
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                        placeholder="RESTORE"
                        className="font-mono"
                        data-testid="confirm-restore-input"
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-3">
                    {importResult.success ? (
                      <Alert className="border-green-200 bg-green-50">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <AlertTitle className="text-green-800">Restore Successful</AlertTitle>
                        <AlertDescription className="text-green-700">
                          {importResult.message}
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Restore Completed with Errors</AlertTitle>
                        <AlertDescription>{importResult.message}</AlertDescription>
                      </Alert>
                    )}
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-muted p-2 rounded">
                        <div className="font-bold">{importResult.collections_restored}</div>
                        <div className="text-muted-foreground text-xs">Collections Restored</div>
                      </div>
                      <div className="bg-muted p-2 rounded">
                        <div className="font-bold">{importResult.documents_restored.toLocaleString()}</div>
                        <div className="text-muted-foreground text-xs">Documents Restored</div>
                      </div>
                    </div>
                    
                    {importResult.errors?.length > 0 && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-red-600">
                          {importResult.errors.length} error(s)
                        </summary>
                        <ul className="mt-1 list-disc list-inside text-red-600 bg-red-50 p-2 rounded">
                          {importResult.errors.map((e, i) => (
                            <li key={`error-${i}-${e.slice(0, 20)}`}>{e}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            {!importResult ? (
              <>
                <Button variant="outline" onClick={closeImportDialog} disabled={importing}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleImport}
                  disabled={confirmText !== "RESTORE" || importing}
                  data-testid="confirm-restore-btn"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Restoring...
                    </>
                  ) : (
                    "Restore Database"
                  )}
                </Button>
              </>
            ) : (
              <Button onClick={closeImportDialog}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
